import React, { useState, useMemo } from 'react';
import { shows } from '../data/shows';

const SHOWS_PER_PAGE = 10; // Number of shows to display per page

const Shows = () => {
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [showPastDates, setShowPastDates] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const { futureShows, pastShows } = useMemo(() => {
        const now = new Date();
        return shows.dates.reduce((acc, show) => {
            const showDate = new Date(show.date);
            if (showDate >= now) {
                acc.futureShows.push(show);
            } else {
                acc.pastShows.push(show);
            }
            return acc;
        }, { futureShows: [], pastShows: [] });
    }, []);

    // Sort shows by date
    futureShows.sort((a, b) => new Date(a.date) - new Date(b.date));
    pastShows.sort((a, b) => new Date(b.date) - new Date(a.date)); // Past shows in reverse order

    // Get unique months from future shows
    const months = [...new Set(futureShows.map(show => {
        return new Date(show.date).toLocaleString('fr-FR', { month: 'long' });
    }))];

    // Filter shows by month
    const filteredShows = useMemo(() => {
        let filtered = selectedMonth === 'all' 
            ? [...futureShows]
            : futureShows.filter(show => {
                const showMonth = new Date(show.date).toLocaleString('fr-FR', { month: 'long' });
                return showMonth === selectedMonth;
            });

        if (showPastDates) {
            filtered = filtered.concat(pastShows);
        }

        return filtered;
    }, [selectedMonth, showPastDates, futureShows, pastShows]);

    // Pagination
    const totalPages = Math.ceil(filteredShows.length / SHOWS_PER_PAGE);
    const currentShows = filteredShows.slice(
        (currentPage - 1) * SHOWS_PER_PAGE,
        currentPage * SHOWS_PER_PAGE
    );

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top of shows section
        document.querySelector('.shows-section').scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section className="shows-section">
            <div className="container">
                <h2>Mon Spectacle</h2>

                {/* Featured Tour Info */}
                {shows.featured && (
                    <div className="featured-tour">
                        {shows.featured.posterImage && (
                            <img 
                                src={shows.featured.posterImage} 
                                alt={shows.featured.title}
                                className="tour-poster"
                            />
                        )}
                        <div className="featured-tour-content">
                            <h3>{shows.featured.title}</h3>
                            <div dangerouslySetInnerHTML={{ __html: shows.featured.description }} />
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="shows-filter">
                    <div className="month-filters">
                        <button 
                            className={`filter-button ${selectedMonth === 'all' ? 'active' : ''}`}
                            onClick={() => setSelectedMonth('all')}
                        >
                            Toutes les dates
                        </button>
                        {months.map(month => (
                            <button 
                                key={month}
                                className={`filter-button ${selectedMonth === month ? 'active' : ''}`}
                                onClick={() => setSelectedMonth(month)}
                            >
                                {month}
                            </button>
                        ))}
                    </div>
                    
                    {pastShows.length > 0 && (
                        <button 
                            className={`past-shows-toggle ${showPastDates ? 'active' : ''}`}
                            onClick={() => setShowPastDates(!showPastDates)}
                        >
                            {showPastDates ? 'Masquer les spectacles passés' : 'Afficher les spectacles passés'}
                        </button>
                    )}
                </div>

                {/* Shows List */}
                <div className="shows-list">
                    {currentShows.map(show => {
                        const showDate = new Date(show.date);
                        const isPast = showDate < new Date();

                        return (
                            <div key={show.id} className={`show-item ${show.status} ${isPast ? 'past' : ''}`}>
                                <div className="show-date">
                                    <span className="day">{showDate.getDate()}</span>
                                    <span className="month">
                                        {showDate.toLocaleString('fr-FR', { month: 'short' })}
                                    </span>
                                </div>
                                
                                <div className="show-info">
                                    <h4>{show.city}</h4>
                                    <p className="venue">{show.venue}</p>
                                    <p className="address">{show.address}</p>
                                </div>

                                <div className="show-time">
                                    <span>{show.status === 'sold_out' ? '' : show.time}&nbsp;&nbsp;&nbsp;</span>
                                    <span className="price">{show.status === 'sold_out' ? '' : show.price}</span>
                                </div>

                                <div className="show-action">
                                    {!isPast && show.status === 'available' ? (
                                        <a 
                                            href={show.ticketLink} 
                                            className="button ticket-button"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Réserver
                                        </a>
                                    ) : (
                                        <span className="status-label">
                                            {isPast ? 'Past Show' : show.status === 'sold_out' ? 'Sold Out' : 'Cancelled'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        {currentPage > 1 && (
                            <button 
                                className="pagination-button"
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                Précédent
                            </button>
                        )}
                        
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index + 1}
                                className={`pagination-button ${currentPage === index + 1 ? 'active' : ''}`}
                                onClick={() => handlePageChange(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}
                        
                        {currentPage < totalPages && (
                            <button 
                                className="pagination-button"
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Suivant
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Shows;